/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { generateId } from "@adapptio/lib-components/src/Functions/generateId";
import { findClosestDraggableId, findClosestDroppableIdFromEvent } from "./utils";
import {
	DRAG_PHASE_TYPE,
	IDragAndDropRegistry,
	IDragEndData,
	IDragEventBinding,
	IDragPhase,
	IDragPoint,
	TDroppableID
} from "./DragAndDropTypes";
import { useDragAndDropRegistry } from "./useDragAndDropRegistry";
import { useDeepCompareMemo } from "use-deep-compare";
import { isFunction } from "@adapptio/lib-shared";

export interface IDragAndDropContext {
	contextId: string;
	registry: IDragAndDropRegistry;
}

export const DragAndDropReactContext = React.createContext<IDragAndDropContext>(null);

export function useDragAndDropContext(): IDragAndDropContext {
	return useContext(DragAndDropReactContext);
}

export interface IDragAndDropContextProps {
	onDragStart?: () => void;
	onDragUpdate?: () => void;
	onDragEnd?: (data: IDragEndData) => void;
}

export const DragAndDropContext: React.FC<IDragAndDropContextProps> = ({
	children,
	onDragStart,
	onDragUpdate,
	onDragEnd
}) => {
	const contextId = useMemo(() => generateId(), []);
	const registry = useDragAndDropRegistry();

	const phase = useRef<IDragPhase>({
		type: DRAG_PHASE_TYPE.IDLE,
		draggableId: null,
		dragging: null,
		draggingOver: null,
		initialPoint: {
			x: 0,
			y: 0
		}
	});

	const getPhase = useCallback(() => phase.current, [ phase ]);

	const setPhase = useCallback(
		(phaseUpdate: Partial<IDragPhase>) => {
			phase.current = {
				...phase.current,
				...phaseUpdate
			};
		},
		[ phase ]
	);

	const move = useCallback<(point: IDragPoint) => void>(
		(point) => {
			const phase = getPhase();

			registry.emitDragMove({
				initialPoint: phase.initialPoint,
				currentPoint: point,
				dragging: phase.dragging,
				draggingOver: phase.draggingOver
			});

			if (isFunction(onDragUpdate)) {
				onDragUpdate();
			}
		},
		[ getPhase, onDragUpdate ]
	);

	const drop = useCallback<(data: { droppableId: TDroppableID; index: number }) => void>(
		({ droppableId, index }) => {
			if (isFunction(onDragEnd)) {
				const phase = getPhase();
				const draggable = registry.draggable.findById(phase.dragging.draggableId);

				onDragEnd({
					destination: droppableId
						? {
								droppableId: droppableId,
								index: index
						  }
						: null,
					source: {
						draggableId: draggable?.droppableId,
						droppableId: draggable?.droppableId,
						index: draggable?.index
					}
				});
			}

			registry.emitDrop();
		},
		[ getPhase, onDragUpdate ]
	);

	const eventBindings = useMemo<IDragEventBinding[]>(
		() => [
			{
				eventName: "mousedown",
				handler: (event: MouseEvent) => {
					// Event already used
					if (event.defaultPrevented) {
						return;
					}
					// only starting a drag if dragging with the primary mouse button
					if (event.button !== 0) {
						return;
					}

					// Do not start a drag if any modifier key is pressed
					if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
						return;
					}

					const draggableId = findClosestDraggableId(contextId, event);

					if (!draggableId) {
						return;
					}

					event.preventDefault();

					const initialPoint: IDragPoint = {
						x: event.clientX,
						y: event.clientY
					};

					const dragging = registry.draggable.findById(draggableId)?.getInfo();

					setPhase({
						type: DRAG_PHASE_TYPE.PENDING,
						draggableId,
						initialPoint,
						dragging
					});
				}
			},
			{
				eventName: "mousemove",
				handler: (event: MouseEvent) => {
					const { button, clientX, clientY } = event;
					if (button !== 0) {
						return;
					}

					const phase = getPhase();

					const point: IDragPoint = {
						x: clientX,
						y: clientY
					};
					// Already dragging
					if (phase.type === DRAG_PHASE_TYPE.DRAGGING) {
						// preventing default as we are using this event
						event.preventDefault();

						const droppableId = findClosestDroppableIdFromEvent(contextId, event);

						setPhase({ draggingOver: droppableId || null });

						move(point);

						return;
					}

					if (phase.type !== DRAG_PHASE_TYPE.PENDING) {
						return;
					}

					event.preventDefault();

					document?.documentElement.style.setProperty("cursor", "grabbing", "important");

					setPhase({ type: DRAG_PHASE_TYPE.DRAGGING });

					if (isFunction(onDragStart)) {
						onDragStart();
					}

					move(point);
				}
			},
			{
				eventName: "mouseup",
				handler: (event: MouseEvent) => {
					const phase = getPhase();

					if (phase.type === DRAG_PHASE_TYPE.PENDING) {
						setPhase({ type: DRAG_PHASE_TYPE.IDLE });
						return;
					}

					if (phase.type !== DRAG_PHASE_TYPE.DRAGGING) {
						setPhase({ type: DRAG_PHASE_TYPE.IDLE });
						return;
					}

					document?.documentElement.style.removeProperty("cursor");

					const droppableId = findClosestDroppableIdFromEvent(contextId, event);

					if (!droppableId) {
						drop({ droppableId: null, index: -1 });
						setPhase({ type: DRAG_PHASE_TYPE.IDLE });

						return;
					}

					const point: IDragPoint = {
						x: event.clientX,
						y: event.clientY
					};

					const draggables = registry.getDraggablesOfDroppable(droppableId);

					let index = 0;

					const dragging = phase.dragging;
					const draggingDraggable = registry.draggable.findById(dragging.draggableId);

					for (let i = 0; i < draggables.length; i++) {
						const draggable = draggables[i];
						const info = draggable.getInfo();

						if (point.y < info.box.marginBox.y) {
							if (draggingDraggable.index > draggable.index) {
								index = draggable.index;
								break;
							}

							break;
						}

						index = draggable.index;
					}

					if (draggingDraggable.droppableId === droppableId && index === draggingDraggable.index) {
						drop({ droppableId: null, index });
						setPhase({ type: DRAG_PHASE_TYPE.IDLE, dragging: null, draggableId: null });

						return;
					}

					drop({ droppableId, index });
					setPhase({ type: DRAG_PHASE_TYPE.IDLE, dragging: null, draggableId: null });
				}
			}
		],
		[ setPhase, getPhase, move, drop ]
	);

	useEffect(() => {
		const unsubscribes = [];

		for (let i = 0; i < eventBindings.length; i++) {
			const eventBinding = eventBindings[i];
			window.addEventListener(eventBinding.eventName, eventBinding.handler, { capture: true });

			unsubscribes.push(() => {
				window.removeEventListener(eventBinding.eventName, eventBinding.handler, { capture: true });
			});
		}

		return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
	}, [ eventBindings ]);

	const contextValue = useDeepCompareMemo<IDragAndDropContext>(
		() => ({
			contextId,
			registry
		}),
		[ contextId, registry ]
	);

	return (
		<DragAndDropReactContext.Provider value={contextValue}>{children}</DragAndDropReactContext.Provider>
	);
};
